import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";

/**
 * Self-service password reset · step 2
 *
 * Reads the one-time token from `?token=…` (out of the email link), accepts
 * a new password, and on success auto-logs the user in by refreshing the
 * auth context (the backend already set the JWT cookie on the same response).
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setErr("Reset link is missing or malformed. Please request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setErr("Your new password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords don't match. Please re-enter.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      // Backend set the JWT cookie on the same response. Refresh the React
      // auth context so the rest of the app picks up the session.
      await refresh?.();
      setDone(true);
      toast.success("Password updated — welcome back.");
      setTimeout(() => nav("/dashboard"), 1500);
    } catch (er) {
      setErr(formatApiError(er.response?.data?.detail) || "Couldn't reset your password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="reset-password-page">
      <SEO noindex title="Set your new password" description="Choose a new password for your Aviana account." />
      <div className="container-aviana max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> Back to sign in
        </Link>

        {!done ? (
          <>
            <p className="overline mb-3">Set new password</p>
            <h1 className="font-serif text-5xl text-aviana-ink leading-tight">Choose a new password.</h1>
            <p className="mt-3 text-aviana-muted">At least 8 characters. After you save, you'll be signed in automatically.</p>

            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="reset-password-form">
              <div>
                <label className="overline">New password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    required
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    className="w-full bg-transparent border-b border-aviana-border focus:border-aviana-gold py-3 pr-10 outline-none"
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-aviana-muted hover:text-aviana-ink p-2"
                    aria-label={show ? "Hide password" : "Show password"}
                    data-testid="toggle-password-visibility"
                  >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="overline">Confirm new password</label>
                <input
                  type={show ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  minLength={8}
                  className="w-full bg-transparent border-b border-aviana-border focus:border-aviana-gold py-3 outline-none"
                  data-testid="confirm-password"
                />
              </div>
              {err && <p className="text-sm text-red-700" data-testid="reset-password-error">{err}</p>}
              <button type="submit" disabled={busy || !token} className="btn-primary w-full" data-testid="reset-password-submit">
                {busy ? "Saving…" : "Set new password"}
              </button>
            </form>

            {!token && (
              <p className="mt-6 text-sm text-aviana-muted">
                <Link to="/forgot-password" className="text-aviana-plum underline underline-offset-4">Request a new reset link →</Link>
              </p>
            )}
          </>
        ) : (
          <div data-testid="reset-password-done">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-aviana-gold/15 text-aviana-goldDeep mb-6">
              <CheckCircle2 size={26} strokeWidth={1.5} />
            </div>
            <h1 className="font-serif text-5xl text-aviana-ink leading-tight">You're back in.</h1>
            <p className="mt-3 text-aviana-muted">Taking you to your dashboard…</p>
          </div>
        )}
      </div>
    </div>
  );
}
